import React, { useState, useEffect, useMemo } from 'react';
import ApiClient, { Channel } from '../api/client';
import ChannelDialog from './ChannelDialog';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import PromptModal from './PromptModal';
import { usePermissions } from '../context/PermissionContext';
import { PageHeader } from './ui';
import './ChannelManager.css';

interface ChannelManagerProps {
  apiClient: ApiClient;
}

type ChannelType = Channel['type'];
type ChannelFilter = 'all' | ChannelType;
type ChannelSortKey = 'type' | 'name' | 'formCount' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const typeOrder: ChannelType[] = ['slack', 'email', 'pushover', 'convertkit', 'mautic', 'twilio-sms', 'espocrm', 'pbx-crm', 'webhook'];
const typeLabels: Record<ChannelType, string> = {
  slack: 'Slack',
  email: 'Email',
  pushover: 'Pushover',
  convertkit: 'ConvertKit',
  mautic: 'Mautic',
  'twilio-sms': 'Twilio SMS',
  espocrm: 'EspoCRM',
  'pbx-crm': 'PBX/IVR',
  webhook: 'Webhook',
};

const ChannelManager: React.FC<ChannelManagerProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [deletingChannelId, setDeletingChannelId] = useState<number | null>(null);
  const [testingChannelId, setTestingChannelId] = useState<number | null>(null);
  const [activeType, setActiveType] = useState<ChannelFilter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: ChannelSortKey; direction: SortDirection }>({
    key: 'type',
    direction: 'asc',
  });

  const canManage = hasPermission('channels.manage');

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.listChannels();
      setChannels(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async (data: {
    name: string;
    type: 'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm';
    config: Record<string, any>;
    testOnCreate?: boolean;
  }) => {
    try {
      const { testOnCreate, ...channelData } = data;
      const newChannel = await apiClient.createChannel(channelData);
      setChannels([...channels, newChannel]);

      if (testOnCreate) {
        try {
          await apiClient.testChannel(newChannel.id);
          setError(null);
        } catch (testErr) {
          setError(
            testErr instanceof Error ? `Channel created but test failed: ${testErr.message}` : 'Channel created but test failed'
          );
        }
      }

      setShowDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create channel');
    }
  };

  const handleUpdateChannel = async (data: {
    name: string;
    type: 'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm';
    config: Record<string, any>;
  }) => {
    if (!editingChannel) return;

    try {
      const updated = await apiClient.updateChannel(editingChannel.id, data);
      setChannels(channels.map((c) => (c.id === editingChannel.id ? updated : c)));
      setEditingChannel(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update channel');
    }
  };

  const handleDeleteChannel = async (id: number) => {
    try {
      await apiClient.deleteChannel(id);
      setChannels(channels.filter((c) => c.id !== id));
      setDeletingChannelId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete channel');
      setDeletingChannelId(null);
    }
  };

  const confirmDelete = (id: number) => {
    setDeletingChannelId(id);
  };

  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const channelList = Array.isArray(channels) ? channels : [];
  const typeCounts = useMemo(() => {
    return channelList.reduce((acc, channel) => {
      acc[channel.type] = (acc[channel.type] ?? 0) + 1;
      return acc;
    }, {} as Record<ChannelType, number>);
  }, [channelList]);

  const filteredChannels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return channelList
      .filter((channel) => activeType === 'all' || channel.type === activeType)
      .filter((channel) => {
        if (!normalizedQuery) return true;
        return (
          channel.name.toLowerCase().includes(normalizedQuery) ||
          typeLabels[channel.type].toLowerCase().includes(normalizedQuery)
        );
      })
      .sort((a, b) => {
        const aValue = getChannelSortValue(a, sort.key);
        const bValue = getChannelSortValue(b, sort.key);
        const result = typeof aValue === 'number' && typeof bValue === 'number'
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
        return sort.direction === 'asc' ? result : -result;
      });
  }, [activeType, channelList, query, sort]);

  const updateSort = (key: ChannelSortKey) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'createdAt' || key === 'formCount' ? 'desc' : 'asc' });
  };

  const sortIndicator = (key: ChannelSortKey) => {
    if (sort.key !== key) return '';
    return sort.direction === 'asc' ? 'up' : 'down';
  };

  const handleTestChannel = async (id: number) => {
    try {
      const channel = channels.find((c) => c.id === id);
      if (!channel) {
        setError('Channel not found');
        return;
      }

      // ConvertKit requires an email for testing
      if (channel.type === 'convertkit') {
        setTestingChannelId(id);
      } else {
        await apiClient.testChannel(id);
        setError(null);
        // Show success message briefly
        setSuccessMsg('✓ Channel test sent successfully!');
        setTimeout(() => setSuccessMsg(null), 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? `Test failed: ${err.message}` : 'Channel test failed');
    }
  };

  const handleTestChannelSubmit = async (email: string) => {
    if (!testingChannelId) return;

    try {
      await apiClient.testChannel(testingChannelId, { email });
      setError(null);
      // Show success message briefly
      setSuccessMsg('✓ Channel test sent successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
      setTestingChannelId(null);
    } catch (err) {
      setError(err instanceof Error ? `Test failed: ${err.message}` : 'Channel test failed');
      setTestingChannelId(null);
    }
  };

  return (
    <div className="manager-page channels-page">
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="alert alert-success">
          <span>{successMsg}</span>
        </div>
      )}

      <PageHeader
        title="Channels"
        actions={canManage && (
          <button className="btn btn-primary" onClick={() => setShowDialog(true)}>
            + Create Channel
          </button>
        )}
      />

      <div className="channels-shell">
        <div className="channels-control-row">
          <select value={activeType} onChange={(event) => setActiveType(event.target.value as ChannelFilter)}>
            <option value="all">All types ({channelList.length})</option>
            {typeOrder
              .filter((type) => (typeCounts[type] ?? 0) > 0)
              .map((type) => (
                <option key={type} value={type}>
                  {typeLabels[type]} ({typeCounts[type]})
                </option>
              ))}
          </select>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search channels..."
          />
          <span>
            Showing {filteredChannels.length} of {channelList.length}
          </span>
        </div>

        {loading ? (
          <div className="empty-state">
            <div className="loading-spinner"></div>
            <p>Loading channels...</p>
          </div>
        ) : channelList.length === 0 ? (
          <div className="empty-state">
            <h3>No channels yet</h3>
            <p>Create a notification channel to receive form submissions.</p>
          </div>
        ) : (
          <div className="channels-table-shell">
            <table className="channels-table">
              <thead>
                <tr>
                  <th>
                    <button className="table-sort-button" onClick={() => updateSort('type')}>Type {sortIndicator('type')}</button>
                  </th>
                  <th>
                    <button className="table-sort-button" onClick={() => updateSort('name')}>Name {sortIndicator('name')}</button>
                  </th>
                  <th className="right">
                    <button className="table-sort-button right" onClick={() => updateSort('formCount')}>Forms {sortIndicator('formCount')}</button>
                  </th>
                  <th>
                    <button className="table-sort-button" onClick={() => updateSort('createdAt')}>Created {sortIndicator('createdAt')}</button>
                  </th>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredChannels.map((channel) => (
                  <tr key={channel.id}>
                    <td>
                      <span className={`channel-type-badge channel-type-${channel.type}`}>{typeLabels[channel.type]}</span>
                    </td>
                    <td className="channel-name">{channel.name}</td>
                    <td className="channel-forms right">{channel.formCount || 0}</td>
                    <td className="channel-created">
                      {channel.createdAt ? new Date(channel.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="channel-actions actions-cell">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleTestChannel(channel.id)}>
                        Test
                      </button>
                      {canManage && (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => setEditingChannel(channel)}>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => confirmDelete(channel.id)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredChannels.length === 0 && (
              <div className="empty-state">
                <h3>No matching channels</h3>
                <p>Clear the search or switch provider tabs.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showDialog && (
        <ChannelDialog
          apiClient={apiClient}
          onClose={() => setShowDialog(false)}
          onSubmit={handleCreateChannel}
        />
      )}

      {editingChannel && (
        <ChannelDialog
          apiClient={apiClient}
          channel={editingChannel}
          onClose={() => setEditingChannel(null)}
          onSubmit={handleUpdateChannel}
        />
      )}

      {deletingChannelId !== null && (
        <ConfirmDeleteModal
          onConfirm={() => handleDeleteChannel(deletingChannelId)}
          onCancel={() => setDeletingChannelId(null)}
        />
      )}

      {testingChannelId !== null && (
        <PromptModal
          title="Test ConvertKit Channel"
          message="Enter an email address to test with. A subscriber will be created/updated with the configured tags."
          placeholder="test@example.com"
          defaultValue="test@example.com"
          onConfirm={handleTestChannelSubmit}
          onCancel={() => setTestingChannelId(null)}
        />
      )}
    </div>
  );
};

function getChannelSortValue(channel: Channel, key: ChannelSortKey): string | number {
  if (key === 'type') return typeOrder.indexOf(channel.type);
  if (key === 'name') return channel.name;
  if (key === 'formCount') return channel.formCount || 0;
  return channel.createdAt ? new Date(channel.createdAt).getTime() : 0;
}

export default ChannelManager;
